// Prompt log component - shows raw AI prompts and responses for debugging
import { useState, useEffect } from 'react';
import { PromptEntry, PromptType } from '../../types';
import './PromptLog.css';

interface PromptLogProps {
  sessionId: string;
  onClose: () => void;
  getPromptLog: (sessionId: string) => Promise<PromptEntry[]>;
}

const PROMPT_TYPE_LABELS: Record<PromptType, string> = {
  opening: 'Opening Scene',
  continue: 'Continue Story',
  death: 'Death Scene',
  memory_summarize: 'Memory Summarize',
  memory_extract: 'Memory Extract Tags'
};

export function PromptLog({ sessionId, onClose, getPromptLog }: PromptLogProps) {
  const [prompts, setPrompts] = useState<PromptEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function loadPrompts() {
      try {
        const data = await getPromptLog(sessionId);
        setPrompts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load prompts');
      } finally {
        setLoading(false);
      }
    }
    loadPrompts();
  }, [sessionId, getPromptLog]);

  const togglePrompt = (id: string) => {
    setExpandedPrompts(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedPrompts(new Set(prompts.map(p => p.id)));
  };

  const collapseAll = () => {
    setExpandedPrompts(new Set());
  };

  if (loading) {
    return (
      <div className="promptlog-overlay" onClick={onClose}>
        <div className="promptlog-modal" onClick={e => e.stopPropagation()}>
          <div className="promptlog-loading">Loading prompts...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="promptlog-overlay" onClick={onClose}>
        <div className="promptlog-modal" onClick={e => e.stopPropagation()}>
          <div className="promptlog-header">
            <h3>Prompt Log</h3>
            <button className="promptlog-close-btn" onClick={onClose}>×</button>
          </div>
          <div className="promptlog-error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="promptlog-overlay" onClick={onClose}>
      <div className="promptlog-modal" onClick={e => e.stopPropagation()}>
        <div className="promptlog-header">
          <h3>Prompt Log</h3>
          <div className="promptlog-header-actions">
            <button className="promptlog-expand-all-btn" onClick={expandAll}>
              Expand All
            </button>
            <button className="promptlog-collapse-all-btn" onClick={collapseAll}>
              Collapse All
            </button>
            <button className="promptlog-close-btn" onClick={onClose}>×</button>
          </div>
        </div>
        
        {prompts.length === 0 ? (
          <div className="promptlog-empty">No prompts recorded yet.</div>
        ) : (
          <div className="promptlog-list">
            {prompts.map((prompt, index) => {
              const isExpanded = expandedPrompts.has(prompt.id);
              return (
                <div key={prompt.id} className={`promptlog-entry ${isExpanded ? 'expanded' : ''}`}>
                  <button 
                    className="promptlog-expand-btn"
                    onClick={() => togglePrompt(prompt.id)}
                  >
                    <span className="promptlog-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                  </button>
                  <div className="promptlog-entry-content">
                    <div className="promptlog-entry-header">
                      <span className="promptlog-scene-number">#{prompt.sceneIndex}</span>
                      <span className="promptlog-type">
                        {PROMPT_TYPE_LABELS[prompt.promptType] || prompt.promptType}
                      </span>
                    </div>
                    
                    {isExpanded ? (
                      <>
                        <div className="promptlog-section">
                          <div className="promptlog-section-header">
                            <span className="promptlog-section-icon">📤</span>
                            <span>System Prompt</span>
                          </div>
                          <pre className="promptlog-content">{prompt.systemPrompt}</pre>
                        </div>
                        
                        <div className="promptlog-section">
                          <div className="promptlog-section-header">
                            <span className="promptlog-section-icon">📥</span>
                            <span>User Prompt</span>
                          </div>
                          <pre className="promptlog-content">{prompt.userPrompt}</pre>
                        </div>
                        
                        <div className="promptlog-section">
                          <div className="promptlog-section-header">
                            <span className="promptlog-section-icon">🤖</span>
                            <span>AI Response</span>
                          </div>
                          <pre className="promptlog-content">{prompt.response}</pre>
                        </div>
                      </>
                    ) : (
                      <div className="promptlog-preview">
                        System: {prompt.systemPrompt.slice(0, 80)}...
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
